import React, { useState, useEffect } from "react";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import { Users, Plus, Trash2, Calendar, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export function DelegatesTab() {
  const { user } = useAuth();
  const [delegations, setDelegations] = useState([]);
  const [actingDelegations, setActingDelegations] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [form, setForm] = useState({ delegate_id: "", delegation_type: "global", contract_id: "", start_date: "", end_date: "", reason: "" });

  useEffect(() => { fetchDelegations(); }, []);

  const fetchDelegations = async () => {
    try {
      const [myDel, actDel, usersRes] = await Promise.all([
        apiClient.get("/delegations"),
        apiClient.get("/delegations/acting"),
        apiClient.get("/users"),
      ]);
      setDelegations(myDel.data);
      setActingDelegations(actDel.data);
      setAllUsers(usersRes.data);
    } catch (e) { /* ok */ }
  };

  const fetchContracts = async () => {
    try { const res = await apiClient.get("/contracts"); setContracts(res.data.items || res.data); } catch (e) { /* ok */ }
  };

  const handleCreate = async () => {
    if (!form.delegate_id) { toast.error("Select a delegate"); return; }
    try {
      const payload = { ...form };
      if (!payload.start_date) delete payload.start_date;
      if (!payload.end_date) delete payload.end_date;
      if (!payload.contract_id) delete payload.contract_id;
      if (!payload.reason) delete payload.reason;
      await apiClient.post("/delegations", payload);
      toast.success("Delegation created");
      setShowDialog(false);
      setForm({ delegate_id: "", delegation_type: "global", contract_id: "", start_date: "", end_date: "", reason: "" });
      fetchDelegations();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create delegation"); }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm("Revoke this delegation?")) return;
    try { await apiClient.delete(`/delegations/${id}`); toast.success("Delegation revoked"); fetchDelegations(); }
    catch (e) { toast.error("Failed to revoke"); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—";

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>My Delegates</CardTitle>
          <CardDescription>Assign delegates who can act on your behalf when you're unavailable</CardDescription>
        </div>
        <Button onClick={() => { setShowDialog(true); fetchContracts(); }} className="gap-2 rounded-sm" data-testid="add-delegation-btn">
          <Plus size={16} /> Add Delegate
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <h3 className="font-medium text-sm text-slate-600 uppercase tracking-wider">Delegations I've Created</h3>
          {delegations.length === 0 ? (
            <p className="text-sm text-slate-400 py-4">No active delegations. Add a delegate for when you're unavailable.</p>
          ) : (
            <div className="space-y-2">
              {delegations.map((d) => (
                <div key={d.id} className={`flex items-center justify-between p-3 border rounded-sm ${d.is_currently_active ? "bg-white dark:bg-slate-900" : "bg-slate-50 dark:bg-slate-800 opacity-60"}`} data-testid={`delegation-${d.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center text-sm font-semibold">
                      {d.delegate_name?.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{d.delegate_name}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        <span className={`px-1.5 py-0.5 rounded ${d.delegation_type === "global" ? "bg-blue-50 text-blue-600" : "bg-amber-50 text-amber-600"}`}>
                          {d.delegation_type === "global" ? "Global" : `Contract: ${d.contract_title || d.contract_id?.substring(0, 8)}`}
                        </span>
                        {d.start_date || d.end_date ? (
                          <span className="flex items-center gap-1"><Calendar size={10} /> {fmtDate(d.start_date)} <ArrowRight size={10} /> {fmtDate(d.end_date)}</span>
                        ) : <span>Open-ended</span>}
                        {d.reason && <span className="italic">— {d.reason}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${d.is_currently_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {d.is_currently_active ? "Active" : d.revoked_at ? "Revoked" : "Expired"}
                    </span>
                    {d.is_active && (
                      <Button variant="ghost" size="sm" className="text-red-500 h-7" onClick={() => handleRevoke(d.id)} data-testid={`revoke-${d.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {actingDelegations.length > 0 && (
            <div className="mt-6">
              <h3 className="font-medium text-sm text-slate-600 uppercase tracking-wider mb-2">Delegations Assigned to Me</h3>
              <div className="space-y-2">
                {actingDelegations.map((d) => (
                  <div key={d.id} className="flex items-center gap-3 p-3 border rounded-sm bg-amber-50/50 dark:bg-amber-900/10" data-testid={`acting-delegation-${d.id}`}>
                    <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-sm font-semibold">
                      {d.delegator_name?.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium">Acting on behalf of {d.delegator_name}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        <span className={`px-1.5 py-0.5 rounded ${d.delegation_type === "global" ? "bg-blue-50 text-blue-600" : "bg-amber-50 text-amber-600"}`}>
                          {d.delegation_type === "global" ? "Global" : `Contract: ${d.contract_title || d.contract_id?.substring(0, 8)}`}
                        </span>
                        {d.start_date || d.end_date ? <span>{fmtDate(d.start_date)} — {fmtDate(d.end_date)}</span> : <span>Open-ended</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {showDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={(e) => { if (e.target === e.currentTarget) setShowDialog(false); }}>
            <div className="bg-white dark:bg-slate-900 rounded-lg p-6 w-full max-w-lg shadow-xl" data-testid="delegation-dialog">
              <h2 className="text-lg font-semibold mb-1">Add Delegation</h2>
              <p className="text-sm text-slate-500 mb-4">Assign someone to act on your behalf for reviews and approvals</p>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Delegate *</Label>
                  <Select value={form.delegate_id} onValueChange={(v) => setForm({ ...form, delegate_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="delegate-user-select"><SelectValue placeholder="Select a user" /></SelectTrigger>
                    <SelectContent>
                      {allUsers.filter((u) => u.id !== user?.id).map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Scope</Label>
                  <Select value={form.delegation_type} onValueChange={(v) => setForm({ ...form, delegation_type: v, contract_id: "" })}>
                    <SelectTrigger className="rounded-sm" data-testid="delegation-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="global">Global (all workflows)</SelectItem>
                      <SelectItem value="per_contract">Per Contract</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.delegation_type === "per_contract" && (
                  <div className="space-y-2">
                    <Label>Contract</Label>
                    <Select value={form.contract_id} onValueChange={(v) => setForm({ ...form, contract_id: v })}>
                      <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select contract" /></SelectTrigger>
                      <SelectContent>
                        {contracts.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Start Date (optional)</Label>
                    <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-sm" data-testid="delegation-start-date" />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date (optional)</Label>
                    <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-sm" data-testid="delegation-end-date" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Reason (optional)</Label>
                  <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g., On leave March 7-14" className="rounded-sm" />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={handleCreate} className="rounded-sm" disabled={!form.delegate_id} data-testid="confirm-delegation-btn">Create Delegation</Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
